import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }
const TIER_COLOR = {
  essential: { bg: '#DBEAFE', fg: '#1E40AF' },
  premium:   { bg: '#FCE7F3', fg: '#9D174D' },
  comercial: { bg: '#D1FAE5', fg: '#065F46' },
}

export default function AdminKits() {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    // Kits + items + costo total
    const { data: kitsData, error } = await supabase
      .from('kits')
      .select(`
        codigo, nombre, tier, personas, precio_susc, precio_unica, costo_estimado, activo, orden,
        items:kit_items(cantidad, producto:productos(sku, nombre, costo_unitario, iva_pct, stock_actual))
      `)
      .order('tier').order('orden').order('nombre')

    if (error) { console.error(error); setLoading(false); return }

    // costoRealCIva: aplica iva_pct específico por producto (10% o 22%)
    const procesados = (kitsData || []).map(k => {
      const items = k.items || []
      const costoRealCIva = items.reduce((s, i) => {
        const cu  = Number(i.producto?.costo_unitario || 0)
        const iva = Number(i.producto?.iva_pct ?? 22)
        return s + cu * (1 + iva / 100) * Number(i.cantidad || 0)
      }, 0)
      const sinStockSuficiente = items.filter(i => {
        const stock = Number(i.producto?.stock_actual || 0)
        return stock < Number(i.cantidad || 0)
      }).length
      return {
        ...k,
        nItems: items.length,
        costoRealCIva,
        sinStockSuficiente,
      }
    })

    setKits(procesados)
    setLoading(false)
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>Kits</h1>
        <p className="lead">Editá la composición de cada kit · Limpack</p>

        {loading ? (
          <div className="card">Cargando…</div>
        ) : kits.length === 0 ? (
          <div className="card">No hay kits cargados.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {kits.map(k => {
              const color = TIER_COLOR[k.tier] || TIER_COLOR.essential
              const margenSusc = Number(k.precio_susc || 0) - k.costoRealCIva
              const margenPct = k.precio_susc > 0 ? (margenSusc / Number(k.precio_susc)) * 100 : 0
              return (
                <Link
                  key={k.codigo}
                  to={`/admin/kits/${k.codigo}`}
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    padding: 16,
                    textDecoration: 'none',
                    color: '#0F1F3D',
                    transition: 'transform .15s, box-shadow .15s',
                    display: 'block',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,31,61,.12)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>{k.nombre}</div>
                    <span style={{
                      background: color.bg, color: color.fg,
                      padding: '3px 10px', borderRadius: 999,
                      fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
                    }}>{TIER_LABEL[k.tier] || k.tier}</span>
                  </div>
                  {k.personas && (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{k.personas}</div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>Susc / mes</div>
                      <div style={{ fontSize: 18, fontWeight: 900 }}>${Number(k.precio_susc || 0).toLocaleString('es-UY')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>Costo c/IVA</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#475569' }}>${Math.round(k.costoRealCIva).toLocaleString('es-UY')}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b' }}>{k.nItems} ítem{k.nItems !== 1 ? 's' : ''}</span>
                    <span style={{
                      color: margenPct > 50 ? '#059669' : margenPct > 35 ? '#D97706' : '#DC2626',
                      fontWeight: 800,
                    }}>Margen {margenPct.toFixed(0)}%</span>
                  </div>
                  {k.sinStockSuficiente > 0 && (
                    <div style={{
                      marginTop: 10, padding: '6px 10px',
                      background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8,
                      fontSize: 11, color: '#7F1D1D', fontWeight: 700,
                    }}>
                      ⚠ {k.sinStockSuficiente} producto{k.sinStockSuficiente !== 1 ? 's' : ''} con stock insuficiente
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
